/**
 * "Seus dados" (docs/refactor/65): the last panel of Configurações › Conta, and the notices the app
 * shows about the account's own state. Three things a person can do to their own data, in plain
 * words: take a copy of it, switch the account off until they sign in again, and have it deleted
 * after a wait of thirty days during which signing in shows "Cancelar exclusão".
 *
 * Nothing here runs on a token alone. Deactivating asks for a recent sign-in (the password again
 * when it is not recent), and deleting always asks for the password or Google: a token lifted from
 * a browser must not be able to end its owner's account.
 */

import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, PauseCircle, Trash2 } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { clearSession } from '../lib/auth'
import { saveFileAs } from '../lib/files'
import { useI18n } from '../lib/i18n'
import { ConfirmIdentity } from './ConfirmIdentity'
import { GoogleSignIn } from './GoogleSignIn'
import { Modal } from './Modal'
import { Panel } from './primitives'

const REACTIVATED_KEY = 'vephus.reactivated'

/** Set by the sign-in page when the server said the account came back; read once by the notice below. */
export function rememberReactivated(): void {
  try {
    sessionStorage.setItem(REACTIVATED_KEY, '1')
  } catch {
    // nothing kept: the notice is a courtesy
  }
}

export function AccountDataPanel() {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const { data: status } = useQuery({ queryKey: ['account'], queryFn: api.account.status, enabled: Boolean(me?.userId && !me.isAgent) })
  const [exporting, setExporting] = useState(false)
  const [exportProblem, setExportProblem] = useState(false)
  const [deactivating, setDeactivating] = useState<'closed' | 'confirm' | 'reauth'>('closed')
  const [deleting, setDeleting] = useState(false)

  const exportData = async (): Promise<void> => {
    setExporting(true)
    setExportProblem(false)
    try {
      const document = await api.account.export()
      await saveFileAs({ name: `vephus-meus-dados-${new Date().toISOString().slice(0, 10)}.json`, content: '' }, JSON.stringify(document, null, 2))
    } catch {
      setExportProblem(true)
    } finally {
      setExporting(false)
    }
  }

  const deactivate = useMutation({
    mutationFn: api.account.deactivate,
    onSuccess: () => {
      // The server ended every session, this one included.
      clearSession()
      navigate('/sign-in', { replace: true, state: { notice: 'deactivated' } })
    },
    onError: (failure) => {
      if (failure instanceof ApiError && failure.code === 'reauth_required') setDeactivating('reauth')
    },
  })

  const cancelDeletion = useMutation({
    mutationFn: api.account.cancelDeletion,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['account'] }).then(() => queryClient.invalidateQueries({ queryKey: ['me'] })),
  })

  // After every hook: an agent key, or nobody signed in, has no account to manage here.
  if (!me?.userId || me.isAgent) return null

  const dueAt = status?.deletionDueAt ? new Date(status.deletionDueAt).toLocaleDateString(locale) : null

  return (
    <Panel title={t('account.data.title')} subtitle={t('account.data.hint')}>
      <div className="stack">
        <p className="small muted" style={{ margin: 0 }}>
          <Link to="/privacy">{t('privacy.title')}</Link> · <Link to="/terms">{t('terms.title')}</Link>
        </p>

        <div className="stack stack--tight">
          <span className="field__label">{t('account.export.title')}</span>
          <span className="tiny muted">{t('account.export.hint')}</span>
          <div className="row row--tight row--wrap">
            <button type="button" className="btn btn--sm" onClick={() => void exportData()} disabled={exporting}>
              <Download size={13} aria-hidden /> {exporting ? t('account.export.working') : t('account.export.button')}
            </button>
            {exportProblem && <span className="tiny" style={{ color: 'var(--danger-text)' }}>{t('error.generic')}</span>}
          </div>
        </div>

        <div className="stack stack--tight">
          <span className="field__label">{t('account.deactivate.title')}</span>
          <span className="tiny muted">{t('account.deactivate.hint')}</span>
          <div className="row row--tight row--wrap">
            <button type="button" className="btn btn--sm" onClick={() => setDeactivating('confirm')} disabled={deactivate.isPending}>
              <PauseCircle size={13} aria-hidden /> {t('account.deactivate.button')}
            </button>
            {deactivate.isError && deactivating === 'closed' && <span className="tiny" style={{ color: 'var(--danger-text)' }}>{t('error.generic')}</span>}
          </div>
        </div>

        <div className="stack stack--tight">
          <span className="field__label">{t('account.delete.title')}</span>
          {dueAt ? (
            <>
              <span className="small" role="status">{t('account.delete.pending', { date: dueAt })}</span>
              <div className="row row--tight row--wrap">
                <button type="button" className="btn btn--sm btn--primary" onClick={() => cancelDeletion.mutate()} disabled={cancelDeletion.isPending}>
                  {t('account.delete.cancel')}
                </button>
              </div>
            </>
          ) : (
            <>
              <span className="tiny muted">{t('account.delete.hint')}</span>
              {status && status.soleOwnerOf.length > 0 && (
                <span className="tiny" style={{ color: 'var(--warn-text)' }}>{t('account.delete.soleOwner', { organizations: status.soleOwnerOf.join(', ') })}</span>
              )}
              <div className="row row--tight row--wrap">
                <button type="button" className="btn btn--sm btn--danger" onClick={() => setDeleting(true)}>
                  <Trash2 size={13} aria-hidden /> {t('account.delete.button')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {deactivating === 'confirm' && (
        <Modal title={t('account.deactivate.title')} onClose={() => setDeactivating('closed')}>
          <div className="stack">
            <p className="small" style={{ margin: 0 }}>{t('account.deactivate.confirmBody')}</p>
            <div className="row row--tight row--wrap">
              <button type="button" className="btn btn--primary" onClick={() => { setDeactivating('closed'); deactivate.mutate() }}>{t('account.deactivate.confirm')}</button>
              <button type="button" className="btn btn--ghost" onClick={() => setDeactivating('closed')}>{t('common.cancel')}</button>
            </div>
          </div>
        </Modal>
      )}
      {deactivating === 'reauth' && (
        <Modal title={t('auth.reauth.title')} onClose={() => setDeactivating('closed')}>
          <ConfirmIdentity why={t('account.deactivate.why')} onConfirmed={() => { setDeactivating('closed'); deactivate.mutate() }} onCancel={() => setDeactivating('closed')} />
        </Modal>
      )}
      {deleting && <DeleteAccountDialog hasPassword={me.hasPassword !== false} onClose={() => setDeleting(false)} />}
    </Panel>
  )
}

/** "Excluir conta": what will happen, then the password (or Google) and the button. */
function DeleteAccountDialog({ hasPassword, onClose }: { hasPassword: boolean; onClose: () => void }) {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { data: config } = useQuery({ queryKey: ['auth', 'config'], queryFn: api.auth.config, staleTime: 60 * 60_000 })

  const remove = useMutation({
    mutationFn: (body: { password?: string; googleIdToken?: string }) => api.account.delete(body),
    onSuccess: (result) => {
      clearSession()
      navigate('/sign-in', { replace: true, state: { notice: 'deletionScheduled', date: new Date(result.deletionDueAt).toLocaleDateString(locale) } })
    },
    onError: (failure) => {
      if (failure instanceof ApiError && failure.code === 'invalid_credentials') setError(t('auth.reauth.wrong'))
      else if (failure instanceof ApiError && failure.code === 'transfer_ownership_first') setError(failure.message)
      else setError(t('error.generic'))
    },
  })

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    if (password) remove.mutate({ password })
  }

  return (
    <Modal title={t('account.delete.title')} onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <p className="small" style={{ margin: 0 }}>{t('account.delete.confirmBody')}</p>
        <ul className="small muted" style={{ margin: 0, paddingLeft: '1.2em' }}>
          <li>{t('account.delete.point1')}</li>
          <li>{t('account.delete.point2')}</li>
          <li>{t('account.delete.point3')}</li>
        </ul>
        {hasPassword ? (
          <label className="field">
            <span className="field__label">{t('auth.reauth.password')}</span>
            <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoFocus data-testid="delete-password" />
          </label>
        ) : (
          <div className="stack stack--tight">
            <span className="tiny muted">{t('account.delete.withGoogle')}</span>
            <GoogleSignIn clientId={config?.googleClientId ?? null} onCredential={(googleIdToken) => remove.mutate({ googleIdToken })} disabled={remove.isPending} />
          </div>
        )}
        {error && <div className="field__error" role="alert">{error}</div>}
        <div className="row row--tight row--wrap">
          {hasPassword && (
            <button type="submit" className="btn btn--danger" disabled={!password || remove.isPending}>
              {remove.isPending ? t('account.delete.working') : t('account.delete.confirm')}
            </button>
          )}
          <button type="button" className="btn btn--ghost" onClick={onClose}>{t('common.cancel')}</button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * Over the signed-in app: "your account will be deleted on …, cancel?" while the wait runs, and
 * "welcome back" once after signing in reactivated it.
 */
export function AccountNotices() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const [reactivated, setReactivated] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(REACTIVATED_KEY) === '1'
    } catch {
      return false
    }
  })
  const cancel = useMutation({
    mutationFn: api.account.cancelDeletion,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['me'] }).then(() => queryClient.invalidateQueries({ queryKey: ['account'] })),
  })

  const dismissReactivated = (): void => {
    try {
      sessionStorage.removeItem(REACTIVATED_KEY)
    } catch {
      // nothing kept
    }
    setReactivated(false)
  }

  if (!me?.userId || me.isAgent) return null
  const dueAt = me.deletionDueAt ? new Date(me.deletionDueAt).toLocaleDateString(locale) : null
  if (!dueAt && !reactivated) return null

  return (
    <div className="account-notices" role="status">
      {dueAt && (
        <div className="account-notice account-notice--danger">
          <span className="small">{t('account.delete.pending', { date: dueAt })}</span>
          <button type="button" className="btn btn--sm btn--primary" onClick={() => cancel.mutate()} disabled={cancel.isPending}>{t('account.delete.cancel')}</button>
        </div>
      )}
      {reactivated && !dueAt && (
        <div className="account-notice">
          <span className="small">{t('account.reactivated')}</span>
          <button type="button" className="btn btn--sm btn--ghost" onClick={dismissReactivated}>{t('common.close')}</button>
        </div>
      )}
    </div>
  )
}
